import type { DataGridColumnProps } from '../../components/DataGrid/DataGrid';
import type { DataGridGetRowLinkOptionsFn } from '../../components/DataGrid/DroppableCell/DroppableCell';
import type { DataGridColumnOptionsProps } from '../../components/DataGridControl/DataGridControl';
import type { SortContentObjectsLocalStorage } from '../../types/localStorage';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getTestProps, setInitialGridOptions } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import CustomizableDataGrid from '../../components/CustomizableDataGrid/CustomizableDataGrid';
import { defaultRenderer } from '../../components/DataGrid/DataGridCell/cellRenderer';
import { useGridFilters } from '../../components/DataGrid/useGridFilters';
import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';
import Loader from '../../components/Loader/Loader';
import ResultsPerPageDropdown from '../../components/Pagination/ResultsPerPageDropdown';
import StatusBar from '../../components/StatusBar/StatusBar';
import Tooltip from '../../components/Tooltip/Tooltip';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';

import { useUserRoles } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useSelectedSpace from '../../hooks/useSelectedSpace';

import UserContext from '../../contexts/UserContext';

import {
  RefreshIcon,
  WarningIcon,
  ZoomMaleWorkingImage,
} from '../../images/shapes';

import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';

const SORT_KEY = 'cms.user-roles-sort';
const OPTIONS_KEY = 'cms.user-roles-grid-state';

const testId = 'user-roles';

const UserRoles = () => {
  const rolesGridContainer = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { buildUrlWithSpace, spaceLimits } = useSelectedSpace();
  const { isAdmin } = use(UserContext);

  const [firstLoading, setFirstLoading] = useState(true);
  const [initDataHasContent, setInitDataHasContent] = useState(false);
  const [columns, setColumns] = useState<DataGridColumnProps[]>([]);

  const [sort, setSort, { removeItem: removeSort }] =
    useLocalStorageState<SortContentObjectsLocalStorage>(SORT_KEY);
  const [gridOptions, setGridOptions, { removeItem: removeGridOptions }] =
    useLocalStorageState<DataGridColumnOptionsProps[]>(OPTIONS_KEY);

  const {
    filters,
    filtersApplied,
    handleFiltersChange,
    page,
    handlePageChange,
    limit,
    handleLimitChange,
    removeAllFilters,
  } = useGridFilters('user-roles');

  const rolesParams = useMemo(
    () => ({
      page,
      limit,
      ...(filters.name?.filter ? { name: filters.name.filter } : {}),
      ...(sort?.sortOrder ? { order_direction: sort?.sortOrder } : {}),
      ...(sort?.sortField ? { order_by: sort?.sortField } : {}),
    }),
    [page, limit, filters.name, sort?.sortOrder, sort?.sortField],
  );

  const rolesOptions = useMemo(
    () => ({
      pause: !isAdmin,
    }),
    [isAdmin],
  );

  const {
    data: roles,
    errors: rolesErrors,
    status: rolesStatus,
    isLoading: rolesAreLoading,
    pagination,
    reload,
  } = useUserRoles(rolesParams, rolesOptions);

  useApiErrorsToast(rolesErrors);

  useEffect(() => {
    if (firstLoading) {
      setInitDataHasContent(false);
    }
    if (!rolesAreLoading) setFirstLoading(false);
  }, [firstLoading, rolesAreLoading]);

  useEffect(() => {
    if (roles.length && !initDataHasContent) {
      setInitDataHasContent(true);
    }
  }, [initDataHasContent, roles.length]);

  useEffect(() => {
    if (pagination?.total_pages < page)
      handlePageChange(pagination.total_pages);
  }, [handlePageChange, page, pagination?.total_pages]);

  const handleResetGrid = useCallback(() => {
    setSort(undefined);
    removeAllFilters();
    removeSort();
    removeGridOptions();
    setGridOptions(undefined);
  }, [
    removeAllFilters,
    removeGridOptions,
    removeSort,
    setGridOptions,
    setSort,
  ]);

  const showGrid = useMemo(
    () =>
      isAdmin &&
      (roles.length > 0 ||
        (rolesAreLoading && !firstLoading) ||
        filtersApplied ||
        initDataHasContent),
    [
      isAdmin,
      roles.length,
      rolesAreLoading,
      firstLoading,
      filtersApplied,
      initDataHasContent,
    ],
  );

  const emptyData = useMemo(() => {
    if (showGrid) return null;
    if (firstLoading && isAdmin) {
      return (
        <Loader
          size="big"
          type="spinner-grid"
          {...getTestProps(testId, 'loading', 'testId')}
        />
      );
    }
    if (rolesStatus === 403 || !isAdmin || rolesErrors)
      return (
        <Heading
          level={2}
          additionalClasses="text-3xl md:text-4xl leading-8 dark:text-white"
        >
          <div
            className="flex flex-col items-center justify-center text-center"
            {...getTestProps(testId, 'empty-data')}
          >
            <WarningIcon className="text-red w-14 md:w-20 mb-3" />
            {rolesStatus === 403 || !isAdmin
              ? t('Global.NoAccess')
              : t('Media.OnErrorMessage')}
          </div>
        </Heading>
      );
    return (
      <>
        <ZoomMaleWorkingImage className="w-52" />
        <div
          className="text-center font-bold p-5"
          {...getTestProps(testId, 'empty-data')}
        >
          <p className="text-blue-600 text-2xl sm:text-3xl md:text-4xl">
            {t('ContentDefinition.CreateFirst')}
          </p>
          <p className="text-slate-900 dark:text-white text-3xl sm:text-4xl md:text-5xl">
            {t('UserRoles.CreateUserRole')}
          </p>
        </div>
        <Tooltip
          tooltip={
            !spaceLimits?.user_roles_enabled ? t('UserRoles.NoCustomRoles') : ''
          }
        >
          <LinkButton
            link={buildUrlWithSpace('user-roles/add')}
            disabled={!spaceLimits?.user_roles_enabled}
            {...getTestProps(testId, 'add-first', 'testId')}
          >
            {t('UserRoles.Add')}
          </LinkButton>
        </Tooltip>
      </>
    );
  }, [
    showGrid,
    firstLoading,
    isAdmin,
    rolesStatus,
    rolesErrors,
    t,
    buildUrlWithSpace,
    spaceLimits?.user_roles_enabled,
  ]);

  const handleInitialGridOptions = useCallback(
    (cols: DataGridColumnProps[], minWidth: number) => {
      setInitialGridOptions(
        cols,
        minWidth,
        rolesGridContainer.current?.offsetWidth || 0,
        setGridOptions,
        setColumns,
      );
    },
    [setGridOptions],
  );

  useEffect(() => {
    const gridOptionsByID = gridOptions?.reduce(
      (acc, elem) => {
        acc[elem.colId] = elem;
        return acc;
      },
      {} as Record<string, DataGridColumnOptionsProps>,
    );

    const cols = [
      {
        accessor: 'name',
        label: t('Global.Name'),
        width: gridOptionsByID?.['name']?.width,
        render: (data: string) => defaultRenderer(data),
        sortable: true,
        filterInputType: 'text',
        resizable: true,
      },
      {
        accessor: 'description',
        label: t('Global.Description'),
        width: gridOptionsByID?.['description']?.width,
        render: (data: string) => defaultRenderer(data),
        resizable: true,
      },
    ];

    if (!gridOptions) {
      handleInitialGridOptions(cols, 250);
    } else {
      setColumns(cols);
    }
  }, [t, handleInitialGridOptions, gridOptions]);

  const getRowLinkOptions = useCallback<DataGridGetRowLinkOptionsFn>(
    (_, rowData) => ({
      to: buildUrlWithSpace(`user-roles/edit/${rowData.id}`),
    }),
    [buildUrlWithSpace],
  );

  const tobarButtonProps = useMemo(() => {
    const defaultProps = {
      label: t('UserRoles.Add'),
      link: buildUrlWithSpace('user-roles/add'),
    };
    if (!spaceLimits?.user_roles_enabled) {
      return {
        ...defaultProps,
        disabled: true,
        tooltip: t('UserRoles.NoCustomRoles'),
        tooltipPlacement: 'leftBottom' as const,
      };
    }
    return defaultProps;
  }, [buildUrlWithSpace, spaceLimits?.user_roles_enabled, t]);

  return (
    <PageLayout
      page="userRoles"
      id="user-roles"
      title={t('Global.UserRoles')}
      breadcrumbs={<TopbarBreadcrumbs />}
      buttons={isAdmin ? <TopbarButton {...tobarButtonProps} /> : null}
      secondaryTopbar={
        <div className="flex items-center justify-end w-full xs:w-auto ml-auto">
          {showGrid && (
            <>
              <Button
                iconImage={<RefreshIcon className="text-blue w-4" />}
                onClick={handleResetGrid}
                buttonColor={'borderless'}
                additionalClasses="text-sm sm:text-base"
                {...getTestProps(testId, 'reset-grid', 'testId')}
              >
                {t('Global.ResetGrid')}
              </Button>
              <ResultsPerPageDropdown
                value={limit}
                setResultsPerPage={handleLimitChange}
                additionalButtonClasses="pr-8! 2xl:py-1!"
                additionalClasses="p-0! ml-5 text-sm sm:text-base"
                label={t('Global.ResultsPerPage')}
                {...getTestProps(testId, 'results-per-page', 'testId')}
              />
            </>
          )}
        </div>
      }
      noPaddings
      testId={testId}
    >
      <div className={predefinedLayoutClasses.withDataGrid}>
        <div
          className={predefinedLayoutClasses.dataGridContainer}
          ref={rolesGridContainer}
        >
          {showGrid ? (
            <CustomizableDataGrid
              sortingLocalStorageKey={SORT_KEY}
              optionsLocalStorageKey={OPTIONS_KEY}
              setCurrentPage={handlePageChange}
              columns={columns}
              data={roles}
              isLoading={rolesAreLoading}
              sort={sort?.sortField}
              sortOrder={sort?.sortOrder}
              statusBar={
                <StatusBar
                  rows={pagination.count}
                  currentPage={page}
                  pagesCount={pagination.total_pages}
                  handlePageChange={handlePageChange}
                  handleDataUpdate={reload}
                  resultsFrom={(pagination.current_page - 1) * limit + 1}
                  resultsTo={
                    (pagination.current_page - 1) * limit + pagination.count
                  }
                  resultsTotalCount={pagination.total_count}
                  {...getTestProps(testId, 'statusbar', 'testId')}
                />
              }
              noDataInfoText={t('UserRoles.FiltersEmptyResult')}
              hasFilters
              filters={filters}
              onFilter={handleFiltersChange}
              getRowLinkOptions={getRowLinkOptions}
              additionalClasses={twMerge('bg-white dark:bg-slate-950 ')}
              {...getTestProps(testId, 'grid', 'testId')}
            />
          ) : (
            <div className={predefinedLayoutClasses.whiteBox}>{emptyData}</div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default UserRoles;
